var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import format from 'string-format';
import { useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import WarningIcon from '@mui/icons-material/Warning';
import { OGTable } from '@opengear/table';
import { useNodes } from '@/api/nodes';
import { useUIPermissions } from '@/api/sessions/self/ui';
import { useFormContainerContext } from '@/components/layout/forms';
import { EnrolledStateCell } from '@/components/nodes/enrolled-state-cell';
import { FreeTextSearch, useFreeTextSearch, useNodeTagsFieldName } from '@/components/free-text-search';
import { NodeFilter, useNodeFilter } from '@/containers/filters/advanced-filters/node-filter';
import { usePaginationModel } from '@/hooks/pagination-model';
import { useSortModel } from '@/hooks/sort-model';
import { NodeStatus, nodeStatusToQuery, productFamilyToQuery, firmwareVersionToQuery, connectionStatusToQuery, useJson, } from '@/utils/query';
import { IQueryNodeField } from '../../../../../../types/query/types';
// Table constants
var statusQuery = nodeStatusToQuery(NodeStatus.Enrolled);
var statusFieldNames = [
    IQueryNodeField.name,
    IQueryNodeField.id,
    IQueryNodeField.lhvpnAddress,
    IQueryNodeField.macAddress,
    IQueryNodeField.model,
    IQueryNodeField.firmwareVersion,
    IQueryNodeField.serialNumber,
    IQueryNodeField.cellHealthStatus,
];
export var SelectCompatibleNodesTable = memo(function (_a) {
    var onFormChange = _a.onFormChange;
    var t = useTranslation().t;
    var _b = useUIPermissions(), hasPermission = _b.hasPermission, hasSomePermission = _b.hasSomePermission;
    var watch = useFormContainerContext().watch;
    var location = useLocation();
    var selectedFirmware = watch('firmware');
    var selectedNodes = watch('nodes');
    var copyData = useMemo(function () { var _a; return (((_a = location.state) === null || _a === void 0 ? void 0 : _a.copyRepeatData) ? __assign({}, location.state.copyRepeatData) : undefined); }, [location.state]);
    var productFamilyQuery = useMemo(function () { return productFamilyToQuery((selectedFirmware === null || selectedFirmware === void 0 ? void 0 : selectedFirmware.family) || (copyData === null || copyData === void 0 ? void 0 : copyData.family) || ''); }, [copyData === null || copyData === void 0 ? void 0 : copyData.family, selectedFirmware === null || selectedFirmware === void 0 ? void 0 : selectedFirmware.family]);
    var firmwareVersionQuery = useMemo(function () { var _a; return (!copyData ? firmwareVersionToQuery((_a = selectedFirmware === null || selectedFirmware === void 0 ? void 0 : selectedFirmware.version) !== null && _a !== void 0 ? _a : '') : undefined); }, [copyData, selectedFirmware === null || selectedFirmware === void 0 ? void 0 : selectedFirmware.version]);
    var readableNodeTags = useMemo(function () { return hasSomePermission(['configure.edit_tags.view', 'configure.node_enrollment.enrollment_bundles.edit']); }, [hasSomePermission]);
    var editableNodeFilters = useMemo(function () { return hasPermission('configure.edit_smart_groups.edit'); }, [hasPermission]);
    var connectionFilterOptions = useMemo(function () { return [
        { value: 'all', name: t('pages.firmware_upgrade_new.tables.compatible_nodes.connection_filter.options.all') },
        { value: 'connected', name: t('pages.firmware_upgrade_new.tables.compatible_nodes.connection_filter.options.connected') },
        { value: 'disconnected', name: t('pages.firmware_upgrade_new.tables.compatible_nodes.connection_filter.options.disconnected') },
        { value: 'failover', name: t('pages.firmware_upgrade_new.tables.compatible_nodes.connection_filter.options.failover') },
    ]; }, [t]);
    var _c = __read(useState('all'), 2), connectionFilterValue = _c[0], setConnectionFilterValue = _c[1];
    var connectionStatusQuery = useMemo(function () { return (connectionFilterValue === 'all' ? null : connectionStatusToQuery(connectionFilterValue)); }, [connectionFilterValue]);
    var handleConnectionFilterChange = useCallback(function (event) {
        var eventValue = event.target.value;
        setConnectionFilterValue(eventValue);
    }, []);
    // Table controls - Server side available query params
    var _d = __read(usePaginationModel('firmware_upgrade_compatible_nodes'), 3), paginationModel = _d[0], handlePaginationModelChange = _d[1], pageSizeOptions = _d[2];
    var _e = __read(useSortModel('id'), 3), defaultSortModel = _e[0], sortQueryParams = _e[1], handleSortModelChange = _e[2];
    // Node filter
    var _f = useNodeFilter(), nodeFilter = _f.nodeFilter, setNodeFilter = _f.setNodeFilter;
    // adding tag fields to fieldnames if user has access to view tags
    var _g = useNodeTagsFieldName(readableNodeTags, statusFieldNames), freeTextDisabled = _g.freeTextDisabled, newFieldNames = _g.newFieldNames;
    // Free text search
    var _h = useFreeTextSearch(newFieldNames), textQueryObject = _h.textQueryObject, dispatchTextQuery = _h.dispatchTextQuery;
    // Aggregate all query items into one json query string
    var json = useJson(statusQuery, productFamilyQuery, firmwareVersionQuery, connectionStatusQuery, nodeFilter === null || nodeFilter === void 0 ? void 0 : nodeFilter.queryObject, textQueryObject);
    var queryParams = useMemo(function () { return (__assign(__assign({ page: paginationModel.page + 1, per_page: paginationModel.pageSize, json: json }, sortQueryParams), { get_upgrades: true })); }, [paginationModel, json, sortQueryParams]);
    // Get Nodes Fetch
    var _j = useNodes(queryParams), data = _j.data, isLoading = _j.isLoading;
    var usedCopyNodes = useMemo(function () {
        if (copyData === null || copyData === void 0 ? void 0 : copyData.nodeIds) {
            return data === null || data === void 0 ? void 0 : data.nodes.filter(function (node) { return (copyData === null || copyData === void 0 ? void 0 : copyData.nodeIds.split(',').includes(node.id)) && node.upgrade_scheduled; });
        }
        return [];
    }, [copyData === null || copyData === void 0 ? void 0 : copyData.nodeIds, data === null || data === void 0 ? void 0 : data.nodes]);
    // Derived table values
    var _k = useMemo(function () { return ({
        loading: isLoading,
        rowCount: data === null || data === void 0 ? void 0 : data.meta.total_count,
        rows: (data === null || data === void 0 ? void 0 : data.nodes) || [],
    }); }, [data === null || data === void 0 ? void 0 : data.meta.total_count, data === null || data === void 0 ? void 0 : data.nodes, isLoading]), loading = _k.loading, rowCount = _k.rowCount, rows = _k.rows;
    var rowSelectionModel = useMemo(function () { return selectedNodes.map(function (node) { return node.id; }); }, [selectedNodes]);
    var handleRowSelectionModelChange = useCallback(function (newRowSelectionModel) {
        var nodeIdsToBeRemoved = rowSelectionModel.filter(function (nodeId) { return !newRowSelectionModel.includes(nodeId); });
        var nodeIdsToBeAdded = newRowSelectionModel.filter(function (nodeId) { return !rowSelectionModel.includes(String(nodeId)); });
        var newSelectedFirmwareNodes = selectedNodes.filter(function (node) { return !nodeIdsToBeRemoved.includes(node.id); });
        nodeIdsToBeAdded.forEach(function (nodeId) {
            var foundNode = rows.find(function (node) { return node.id === nodeId; });
            if (foundNode) {
                newSelectedFirmwareNodes.push(__assign(__assign({}, foundNode), { allowInFailover: false }));
            }
        });
        onFormChange(newSelectedFirmwareNodes);
    }, [onFormChange, rowSelectionModel, rows, selectedNodes]);
    // Column grid
    var columns = useMemo(function () { return [
        {
            field: 'connection_status',
            headerName: t('pages.firmware_upgrade_new.tables.compatible_nodes.headers.state'),
            maxWidth: 1,
            minWidth: 85,
            renderCell: function (_a) {
                var row = _a.row;
                return _jsx(EnrolledStateCell, { runtime_status: row.runtime_status });
            },
        },
        { field: 'id', headerName: t('pages.firmware_upgrade_new.tables.compatible_nodes.headers.id'), minWidth: 140 },
        { field: 'name', headerName: t('pages.firmware_upgrade_new.tables.compatible_nodes.headers.name'), flex: 1, minWidth: 160 },
        {
            field: 'model',
            headerName: t('pages.firmware_upgrade_new.tables.compatible_nodes.headers.model'),
            valueFormatter: function (_a) {
                var value = _a.value;
                return value !== null && value !== void 0 ? value : 'N/A';
            },
            flex: 1,
            minWidth: 160,
        },
        {
            field: 'firmware_version',
            headerName: t('pages.firmware_upgrade_new.tables.compatible_nodes.headers.firmware_version'),
            flex: 1,
            minWidth: 200,
        },
    ]; }, [t]);
    return (_jsxs(_Fragment, { children: [(usedCopyNodes === null || usedCopyNodes === void 0 ? void 0 : usedCopyNodes.length) ? (_jsx(Box, __assign({ marginY: 2 }, { children: _jsxs(Box, __assign({ sx: { display: 'flex', alignItems: 'center' } }, { children: [_jsx(WarningIcon, { color: "warning", sx: { marginRight: 0.5 } }), _jsx(Typography, { children: format(t('pages.firmware_upgrade_new.tables.compatible_nodes.nodes_warning_message'), usedCopyNodes.map(function (node) { return node.id; }).join(', ')) })] })) }))) : null, _jsxs(Stack, __assign({ direction: "row", marginBottom: 1, marginTop: 1, justifyContent: "space-between", flexWrap: "wrap" }, { children: [_jsxs(Stack, __assign({ direction: "row" }, { children: [_jsx(Box, __assign({ marginRight: 1 }, { children: _jsx(NodeFilter, { filter: nodeFilter, setFilter: setNodeFilter, permissions: { hasSmartgroupsEditAccess: editableNodeFilters } }) })), _jsx(TextField, __assign({ select: true, onChange: handleConnectionFilterChange, sx: { width: '180px', margin: 0 }, inputProps: { 'aria-label': t('pages.firmware_upgrade_new.tables.compatible_nodes.connection_status_filter.label') }, value: connectionFilterValue }, { children: connectionFilterOptions.map(function (option) { return (_jsx(MenuItem, __assign({ value: option.value }, { children: option.name }), option.value)); }) }))] })), _jsx(FreeTextSearch, { onSearch: dispatchTextQuery, freeTextDisabled: freeTextDisabled, useDebounce: false, translations: { tooltip: t('components.nodes.text_search.tooltip') } })] })), _jsx(Box, __assign({ marginBottom: 1 }, { children: _jsx(OGTable, { "aria-label": t('pages.firmware_upgrade_new.tables.compatible_nodes.label'), autoHeight: true, columns: columns, rows: rows, loading: loading, noRowsMessage: t('pages.firmware_upgrade_new.tables.compatible_nodes.no_row_message'), pageSizeOptions: pageSizeOptions, pagination: true, paginationMode: "server", paginationModel: paginationModel, onPaginationModelChange: handlePaginationModelChange, selectedRowCount: selectedNodes.length, translations: { entityLabel: t('global.table.node') }, rowCount: rowCount, sortingMode: "server", onSortModelChange: handleSortModelChange, checkboxSelection: true, rowSelectionModel: rowSelectionModel, keepNonExistentRowsSelected: true, onRowSelectionModelChange: handleRowSelectionModelChange, isRowSelectable: function (params) { return params.row.upgrade_scheduled === null; }, initialState: {
                        sorting: {
                            sortModel: defaultSortModel,
                        },
                    } }) }))] }));
});
